import QueryBuilder from '~/sqlite/services/queryBuilder';
import { Schemas } from '~/sqlite/schemas';
import { tableMigrationRegistry } from './TableMigrationRegistry';

import { DatabaseClient } from './DatabaseClient';
import { LATEST_SCHEMA_VERSION } from './migrations/constants';
import { runFullSchemaReset } from './migrations/outboxRestore';
import { getUserVersion } from './migrations/userVersion';

export { LATEST_SCHEMA_VERSION } from './migrations/constants';

export type SchemaMigrateResult = {
  migrated: boolean;
};

function formatAlterDefault(defaultValue: string | number | null | undefined): string {
  if (defaultValue === undefined) {
    return '';
  }
  if (defaultValue === null) {
    return ' DEFAULT null';
  }
  if (typeof defaultValue === 'number') {
    return ` DEFAULT ${defaultValue}`;
  }
  return ` DEFAULT '${String(defaultValue).replace(/'/g, "''")}'`;
}

export class SchemaMigrator {
  private qb = new QueryBuilder();

  async migrateIfNeeded(client: DatabaseClient): Promise<SchemaMigrateResult> {
    let migrated = false;
    const currentVersion = await getUserVersion(client);
    if (currentVersion < LATEST_SCHEMA_VERSION) {
      await runFullSchemaReset(client, LATEST_SCHEMA_VERSION);
      migrated = true;
    }

    await this.ensureTables(client);
    return { migrated };
  }

  async ensureTables(client: DatabaseClient): Promise<void> {
    await client.transactionAsync((tx) => {
      tableMigrationRegistry.createTableStatements().forEach((sql) => {
        tx.executeSql(sql);
      });
    });

    for (const [tableName, columns] of Object.entries(Schemas)) {
      try {
        const info = await client.executeSql(`PRAGMA table_info(${tableName})`, []);
        const existingColumns = new Set<string>();
        if (info && info.rows) {
          for (let i = 0; i < info.rows.length; i++) {
            existingColumns.add(info.rows.item(i).name);
          }
        }
        for (const col of columns) {
          if (!existingColumns.has(col.name)) {
            const colType = col.type === 'boolean' ? 'integer' : col.type;
            const defVal = formatAlterDefault(col.defaultValue);
            await client.executeSql(
              `ALTER TABLE ${tableName} ADD COLUMN ${col.name} ${colType}${defVal}`,
              []
            );
          }
        }
      } catch (err) {
        console.warn(`[SchemaMigrator] ensure columns failed for ${tableName}:`, err);
      }
    }
  }
}

export const schemaMigrator = new SchemaMigrator();
